import { ref } from 'vue'

// Global notification state
const notifications = ref([])
let notificationIdCounter = 0

// Notification types
export const NotificationTypes = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
}

// Default durations (in milliseconds)
export const NotificationDurations = {
  SUCCESS: 5000,
  ERROR: 8000,
  WARNING: 6000,
  INFO: 5000
}

// Composable for notification system
export function useNotifications() {
  
  // Add a new notification
  const addNotification = (type, title, message, details = null, duration = null) => {
    const id = ++notificationIdCounter
    const notificationDuration = duration || NotificationDurations[type.toUpperCase()] || 5000
    
    const notification = {
      id,
      type,
      title,
      message,
      details,
      duration: notificationDuration
    }
    
    notifications.value.push(notification)
    
    // Auto-remove after duration
    setTimeout(() => {
      removeNotification(id)
    }, notificationDuration)
    
    return id
  }
  
  // Remove a notification by ID
  const removeNotification = (id) => {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index > -1) {
      notifications.value.splice(index, 1)
    }
  }
  
  // Clear all notifications
  const clearAllNotifications = () => {
    notifications.value = []
  }
  
  // Convenience methods for different notification types
  const showSuccess = (title, message, details = null, duration = null) => {
    return addNotification(NotificationTypes.SUCCESS, title, message, details, duration)
  }
  
  const showError = (title, message, details = null, duration = null) => {
    return addNotification(NotificationTypes.ERROR, title, message, details, duration)
  }
  
  const showWarning = (title, message, details = null, duration = null) => {
    return addNotification(NotificationTypes.WARNING, title, message, details, duration)
  }
  
  const showInfo = (title, message, details = null, duration = null) => {
    return addNotification(NotificationTypes.INFO, title, message, details, duration)
  }
  
  const GENERIC_API_MESSAGES = new Set([
    'the given data was invalid.',
    'the given data was invalid',
    'validation failed.',
    'validation failed',
    'server error. please try again later.',
    'an unexpected error occurred. please try again.',
  ])

  const humanizeFieldName = (field) => {
    if (!field || typeof field !== 'string') return 'Field'
    const cleaned = field
      .replace(/\.\d+/g, '')
      .replace(/\[\]/g, '')
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
  }

  const isGenericApiMessage = (message) => {
    if (!message || typeof message !== 'string') return true
    return GENERIC_API_MESSAGES.has(message.trim().toLowerCase())
  }

  const pushUnique = (list, value) => {
    const text = String(value || '').trim()
    if (!text) return
    if (!list.some((item) => item.toLowerCase() === text.toLowerCase())) {
      list.push(text)
    }
  }

  const collectErrorBagDetails = (errorData, details = []) => {
    if (!errorData || typeof errorData !== 'object') return details

    if (errorData.errors && typeof errorData.errors === 'object') {
      Object.keys(errorData.errors).forEach((field) => {
        const fieldErrors = Array.isArray(errorData.errors[field])
          ? errorData.errors[field]
          : [errorData.errors[field]]

        fieldErrors.forEach((err) => {
          const message = String(err || '').trim()
          if (!message) return

          // Laravel messages usually already name the attribute.
          if (/^the\s+/i.test(message)) {
            pushUnique(details, message)
          } else {
            pushUnique(details, `${humanizeFieldName(field)}: ${message}`)
          }
        })
      })
    }

    // Prefer the human-facing API message before raw exception text.
    if (errorData.message && !isGenericApiMessage(errorData.message)) {
      pushUnique(details, errorData.message)
    } else if (errorData.message && details.length === 0) {
      pushUnique(details, errorData.message)
    }

    // Explicit server/exception detail (common on 500 responses).
    if (errorData.error && typeof errorData.error === 'string') {
      pushUnique(details, errorData.error)
    }

    if (Array.isArray(errorData.details)) {
      errorData.details.forEach((item) => pushUnique(details, item))
    }

    return details
  }

  // Format Laravel / API validation and failure details into readable lines
  const formatValidationErrors = (error) => {
    const details = []
    const responseData = error?.response?.data
    const status = error?.response?.status

    collectErrorBagDetails(responseData, details)

    // Plain Error / thrown objects without axios response
    if (!responseData && error?.message && !isGenericApiMessage(error.message)) {
      pushUnique(details, error.message)
    }

    // Network / transport issues (only when no useful payload yet)
    if (details.length === 0) {
      if (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error') {
        pushUnique(
          details,
          'Network connection error. Please check your internet connection and try again.',
        )
      } else if (error?.code === 'ECONNABORTED') {
        pushUnique(details, 'The request timed out. Please try again.')
      } else if (status === 401) {
        pushUnique(details, 'Session expired. Please log in again.')
      } else if (status === 403) {
        pushUnique(details, 'You do not have permission to perform this action.')
      } else if (status === 404) {
        pushUnique(details, 'The requested resource was not found.')
      } else if (status === 413) {
        pushUnique(
          details,
          'One or more uploaded files are too large. Please upload smaller files and try again.',
        )
      } else if (status === 422) {
        pushUnique(
          details,
          'Some fields are invalid. Please review the form and correct the highlighted issues.',
        )
      } else if (status >= 500) {
        pushUnique(details, 'Server error. Please try again later.')
      }
    }

    if (details.length === 0) {
      pushUnique(details, 'An unexpected error occurred. Please try again.')
    }

    return details
  }

  // Handle API errors with automatic notification (first detail as message)
  const handleApiError = (error, customTitle = 'Error') => {
    const details = formatValidationErrors(error)
    return showError(customTitle, details[0], details.slice(1), NotificationDurations.ERROR)
  }

  // Detailed submission failure notification for public application forms
  const notifySubmissionFailure = (errorOrBody, customTitle = 'Submission failed') => {
    let details = []

    if (errorOrBody?.response || errorOrBody?.isAxiosError || errorOrBody instanceof Error) {
      details = formatValidationErrors(errorOrBody)
    } else if (errorOrBody && typeof errorOrBody === 'object') {
      details = collectErrorBagDetails(errorOrBody, [])
      if (details.length === 0) {
        pushUnique(
          details,
          errorOrBody.message || 'Unable to submit your application. Please try again.',
        )
      }
    } else if (typeof errorOrBody === 'string') {
      pushUnique(details, errorOrBody)
    }

    if (details.length === 0) {
      pushUnique(details, 'Unable to submit your application. Please try again.')
    }

    // Keep longer so applicants can read field-level issues.
    return showError(customTitle, details[0], details.slice(1), 16000)
  }
  
  // Get notification icon
  const getNotificationIcon = (type) => {
    const icons = {
      [NotificationTypes.SUCCESS]: 'mdi-check-circle',
      [NotificationTypes.ERROR]: 'mdi-alert-circle',
      [NotificationTypes.WARNING]: 'mdi-alert',
      [NotificationTypes.INFO]: 'mdi-information'
    }
    return icons[type] || 'mdi-information'
  }
  
  // Get notification icon color
  const getNotificationIconColor = (type) => {
    const colors = {
      [NotificationTypes.SUCCESS]: 'white',
      [NotificationTypes.ERROR]: 'white',
      [NotificationTypes.WARNING]: 'white',
      [NotificationTypes.INFO]: 'white'
    }
    return colors[type] || 'white'
  }
  
  return {
    // Reactive data
    notifications,
    
    // Methods
    addNotification,
    removeNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    formatValidationErrors,
    handleApiError,
    notifySubmissionFailure,
    getNotificationIcon,
    getNotificationIconColor,
    
    // Constants
    NotificationTypes,
    NotificationDurations
  }
}
